import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Camera, PieChart, Compass, UtensilsCrossed, TrendingUp, Sparkles, Check } from "lucide-react";
import { Reveal, Label } from "../components/site/Reveal";
import { KineticLines } from "../components/site/KineticLines";
import { Marquee } from "../components/site/Marquee";
import { AppStoreBadge } from "../components/site/AppStoreBadge";
import { WaitlistForm } from "../components/site/WaitlistForm";
import { SITE } from "../config/site";

const IMAGES = {
  hero: "https://images.unsplash.com/photo-1522125670776-3c7abb882bc2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxwZXJzb24lMjB1c2luZyUyMHNtYXJ0cGhvbmUlMjBzdW5ueXxlbnwwfHx8fDE3ODc3ODE4OTh8MA&ixlib=rb-4.1.0&q=85",
  photo: "https://images.unsplash.com/photo-1782939178158-877d649b4178?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxmcmVzaCUyMHNhbGFkJTIwY2xlYW4lMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc4Nzc4MTg5OHww&ixlib=rb-4.1.0&q=85",
  macros: "https://images.unsplash.com/photo-1761315600943-d8a5bb0c499f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwyfHxmcmVzaCUyMHNhbGFkJTIwY2xlYW4lMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc4Nzc4MTg5OHww&ixlib=rb-4.1.0&q=85",
  guidance: "https://images.unsplash.com/photo-1747292718361-c838a9968ec7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxoZWFsdGh5JTIwbWVhbCUyMHByZXAlMjBvdmVyaGVhZHxlbnwwfHx8fDE3ODc3ODE4OTh8MA&ixlib=rb-4.1.0&q=85",
  coach: "https://images.unsplash.com/photo-1572016047668-5b5e909e1605?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxwZXJzb24lMjB1c2luZyUyMHNtYXJ0cGhvbmUlMjBzdW5ueXxlbnwwfHx8fDE3ODc3ODE4OTh8MA&ixlib=rb-4.1.0&q=85",
};

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const blobY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} data-testid="hero-section" className="noise relative overflow-hidden bg-cream pt-32 md:pt-44 pb-20 md:pb-28">
      <motion.div
        style={{ y: blobY }}
        aria-hidden="true"
        className="pointer-events-none absolute -right-40 -top-40 h-[34rem] w-[34rem] rounded-full bg-coral/10 blur-3xl"
      />
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-6 md:px-10 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mb-8 inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-neutral-500"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-teal" aria-hidden="true" />
            Nutrition · Macro Tracking · AI Meal Planning
          </motion.div>

          <h1 className="font-display text-5xl font-medium leading-[1.02] tracking-tight text-neutral-900 sm:text-6xl lg:text-7xl">
            <KineticLines
              lines={[
                "Your Next Plate,",
                "Already Planned.",
              ]}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 max-w-xl text-base leading-relaxed text-neutral-600 md:text-lg"
          >
            Stop wondering what to eat next. {SITE.name} tracks what you eat, understands what you
            still need, and helps guide your next meal.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap items-center gap-5"
          >
            <AppStoreBadge />
            <span className="text-sm text-neutral-400">{SITE.launchNote} · Free to start</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10"
          >
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-neutral-400">
              Get NXTPlate updates
            </p>
            <WaitlistForm source="hero" testId="hero-waitlist" />
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 60, rotate: 4 }}
            animate={{ opacity: 1, y: 0, rotate: 2 }}
            transition={{ duration: 1.1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <motion.div style={{ y: imgY }} className="relative overflow-hidden rounded-[2rem] shadow-[0_24px_48px_rgba(0,0,0,0.10)]">
              <img
                src={IMAGES.hero}
                alt="A person checking their phone on a sunny day"
                className="aspect-[4/5] w-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/25 via-transparent to-transparent" aria-hidden="true" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1, ease: [0.22, 1, 0.36, 1] }}
              data-testid="hero-remaining-card"
              className="absolute -bottom-8 -left-6 md:-left-14 rounded-2xl border border-neutral-100 bg-white/90 p-5 shadow-[0_16px_40px_rgba(0,0,0,0.12)] backdrop-blur-xl"
            >
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-neutral-400">Still left today</p>
              <div className="mt-3 space-y-2.5">
                {[["Protein", "42 g", "w-4/5", "bg-coral"], ["Carbs", "110 g", "w-3/5", "bg-teal"], ["Fat", "28 g", "w-2/5", "bg-amber-400"]].map(([n, v, w, c]) => (
                  <div key={n} className="flex items-center gap-3">
                    <span className="w-14 text-xs font-medium text-neutral-600">{n}</span>
                    <span className="h-1.5 w-28 overflow-hidden rounded-full bg-neutral-100">
                      <motion.span
                        className={`block h-full rounded-full ${c} ${w}`}
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ duration: 1.2, delay: 1.3, ease: [0.22, 1, 0.36, 1] }}
                        style={{ transformOrigin: "left" }}
                      />
                    </span>
                    <span className="text-xs font-semibold text-neutral-900">{v}</span>
                  </div>
                ))}
              </div>
              <p className="mt-3 border-t border-neutral-100 pt-3 text-xs text-neutral-500">
                Next up: a protein-forward dinner
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const FeatureCard = ({ index, icon: Icon, title, copy, children, className = "" }) => (
  <Reveal className={`group ${className}`} delay={(index % 3) * 0.08}>
    <div
      data-testid={`feature-card-${index}`}
      className="flex h-full flex-col overflow-hidden rounded-3xl border border-neutral-200/70 bg-white p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04)] transition-shadow duration-300 hover:shadow-[0_24px_48px_rgba(0,0,0,0.08)]"
    >
      <div className="flex items-center justify-between">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-bone text-neutral-800 transition-colors duration-300 group-hover:bg-coral group-hover:text-white">
          <Icon className="h-5 w-5" aria-hidden="true" />
        </span>
        <span className="font-display text-sm italic text-neutral-300">0{index}</span>
      </div>
      <h3 className="mt-6 font-display text-2xl font-medium tracking-tight text-neutral-900">{title}</h3>
      <p className="mt-3 text-sm leading-relaxed text-neutral-600 md:text-base">{copy}</p>
      {children && <div className="mt-auto pt-8">{children}</div>}
    </div>
  </Reveal>
);

const ClippedImage = ({ src, alt, ratio = "aspect-[16/10]" }) => (
  <div className={`overflow-hidden rounded-2xl ${ratio}`}>
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
    />
  </div>
);

const RemainingViz = () => (
  <div data-testid="remaining-viz" className="rounded-2xl bg-bone p-6">
    <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-neutral-400">Remaining today</p>
    <div className="mt-5 space-y-4">
      {[["Protein", 78, "42 g left", "bg-coral"], ["Carbs", 55, "110 g left", "bg-teal"], ["Fat", 38, "28 g left", "bg-amber-400"]].map(([n, pct, v, c]) => (
        <div key={n}>
          <div className="mb-1.5 flex items-baseline justify-between">
            <span className="text-sm font-medium text-neutral-700">{n}</span>
            <span className="text-xs font-semibold text-neutral-900">{v}</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-white">
            <motion.div
              className={`h-full rounded-full ${c}`}
              initial={{ width: 0 }}
              whileInView={{ width: `${pct}%` }}
              viewport={{ once: true }}
              transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            />
          </div>
        </div>
      ))}
    </div>
    <p className="mt-5 text-xs leading-relaxed text-neutral-500">
      The day reads like a plan — not a ledger of what already happened.
    </p>
  </div>
);

const ProgressViz = () => (
  <div data-testid="progress-viz" className="rounded-2xl bg-bone p-6">
    <div className="flex items-baseline justify-between">
      <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-neutral-400">12-week consistency</p>
      <p className="font-display text-2xl font-medium text-neutral-900">86%</p>
    </div>
    <svg viewBox="0 0 320 90" className="mt-4 w-full" role="img" aria-label="Progress trend line moving upward over twelve weeks">
      <motion.polyline
        points="0,70 30,64 60,66 90,54 120,58 150,44 180,48 210,36 240,38 270,24 320,14"
        fill="none"
        stroke="#FF6B57"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={{ pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.6, ease: "easeInOut" }}
      />
      <circle cx="320" cy="14" r="5" fill="#2A9D8F" />
    </svg>
    <p className="mt-3 text-xs leading-relaxed text-neutral-500">
      Trends, streaks and weekly patterns — progress you can actually feel.
    </p>
  </div>
);

const Features = () => (
  <section id="features" data-testid="features-section" className="bg-cream py-24 md:py-32">
    <div className="mx-auto max-w-7xl px-6 md:px-10">
      <Reveal><Label>What NXTPlate does</Label></Reveal>
      <Reveal delay={0.08}>
        <h2 className="mt-6 max-w-3xl font-display text-4xl font-medium leading-tight tracking-tight text-neutral-900 md:text-5xl">
          Everything between <span className="italic text-coral">this meal</span> and the next one.
        </h2>
      </Reveal>

      <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-8">
        <FeatureCard index={1} icon={Camera} title="Photo-based food logging" copy="Snap your plate and let NXTPlate do the typing. Photo logging turns a meal into macros in seconds, and you always review before anything is saved." className="md:col-span-7">
          <ClippedImage src={IMAGES.photo} alt="Fresh colorful vegetables arranged on a clean background" />
        </FeatureCard>
        <FeatureCard index={2} icon={PieChart} title="Macro & calorie tracking" copy="Calories, protein, carbs and fat — logged automatically from photos or search, and tallied against your daily targets." className="md:col-span-5">
          <ClippedImage src={IMAGES.macros} alt="Grilled chicken salad with avocado" ratio="aspect-[16/11]" />
        </FeatureCard>
        <FeatureCard index={3} icon={Compass} title="Remaining nutrition for the day" copy="NXTPlate keeps a live picture of what your day still needs, so every choice after lunch is an informed one." className="md:col-span-5">
          <RemainingViz />
        </FeatureCard>
        <FeatureCard index={4} icon={UtensilsCrossed} title="Personalized next-meal guidance" copy="Based on what you've eaten and what's still left, NXTPlate suggests a practical direction for your next plate — not a generic meal plan." className="md:col-span-7">
          <ClippedImage src={IMAGES.guidance} alt="A colorful healthy bowl seen from overhead" />
        </FeatureCard>
        <FeatureCard index={5} icon={TrendingUp} title="Progress tracking" copy="Consistency, trends and weekly patterns in one calm view — so you see the arc of your habits, not just single days." className="md:col-span-6">
          <ProgressViz />
        </FeatureCard>
        <FeatureCard index={6} icon={Sparkles} title="NXTPlate AI Coach" copy="An AI coach that understands your targets, your history and your preferences — and turns them into guidance that adapts with you." className="md:col-span-6">
          <ClippedImage src={IMAGES.coach} alt="A person checking an app on their phone outdoors" ratio="aspect-[16/10]" />
        </FeatureCard>
      </div>
    </div>
  </section>
);

const chapters = [
  {
    n: "01",
    title: "See the whole day at a glance",
    copy: "Most trackers bury you in numbers. NXTPlate turns what you've eaten into a clear picture of what's still left — calories, protein, carbs and fat — so the day reads like a plan, not a ledger.",
  },
  {
    n: "02",
    title: "From data to direction",
    copy: "Logging is only half the job. NXTPlate converts your remaining nutrition into a practical next-step recommendation, so the question \"what should I eat?\" already has an answer waiting.",
  },
  {
    n: "03",
    title: "Guidance that adapts with you",
    copy: "With NXTPlate AI Coach, your targets, trends and preferences shape the guidance you get — meal after meal, week after week. Less tracking for the sake of tracking. Better decisions, made sooner.",
  },
];

const Manifesto = () => (
  <section id="why" data-testid="manifesto-section" className="bg-white py-24 md:py-36">
    <div className="mx-auto max-w-7xl px-6 md:px-10">
      <Reveal><Label>Why NXTPlate</Label></Reveal>
      <div className="mt-6 grid grid-cols-1 gap-16 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-32">
            <Reveal delay={0.08}>
              <h2 className="font-display text-4xl font-medium leading-tight tracking-tight text-neutral-900 md:text-6xl">
                Track less.<br />
                <span className="italic text-coral">Decide better.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-8 max-w-md text-base leading-relaxed text-neutral-600 md:text-lg">
                A basic calorie tracker tells you what you did. NXTPlate tells you what to do next —
                turning nutrition data into a practical next-step recommendation.
              </p>
            </Reveal>
          </div>
        </div>
        <div className="lg:col-span-7">
          {chapters.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.06} className="border-t border-neutral-200 py-12 first:border-t-0 first:pt-0 md:py-16">
              <div data-testid={`manifesto-chapter-${i + 1}`} className="flex gap-8">
                <span className="font-display text-5xl font-medium italic leading-none text-neutral-200 md:text-7xl">
                  {c.n}
                </span>
                <div>
                  <h3 className="font-display text-2xl font-medium tracking-tight text-neutral-900 md:text-3xl">
                    {c.title}
                  </h3>
                  <p className="mt-4 max-w-lg text-base leading-relaxed text-neutral-600">{c.copy}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const tiers = [
  {
    name: "Free",
    tag: "Basic access",
    blurb: "Everything you need to start understanding your plate.",
    monthly: 0,
    yearly: 0,
    features: ["Photo-based food logging", "Calorie & macro tracking", "Remaining nutrition for the day", "Basic progress history"],
    highlight: false,
    testId: "plan-card-free",
  },
  {
    name: "NXTPlate Pro",
    tag: "For consistent trackers",
    blurb: "Deeper targets, longer history, sharper trends.",
    monthly: 14.99,
    yearly: 143.99,
    features: ["Everything in Free", "Advanced macro goals", "Detailed progress trends", "Full photo & logging history"],
    highlight: true,
    testId: "plan-card-pro",
  },
  {
    name: "NXTPlate AI Coach",
    tag: "The full experience",
    blurb: "Your data, turned into day-to-day direction.",
    monthly: 24.99,
    yearly: 239.99,
    features: ["Everything in Pro", "Personalized next-meal guidance", "AI Coach check-ins", "Adaptive daily recommendations"],
    highlight: false,
    accent: true,
    testId: "plan-card-ai-coach",
  },
];

const PriceLine = ({ tier, cycle }) => {
  if (!tier.monthly) {
    return (
      <div className="mt-6 flex items-baseline gap-2">
        <span className="font-display text-5xl font-medium tracking-tight text-neutral-900">$0</span>
        <span className="text-sm text-neutral-500">free access</span>
      </div>
    );
  }
  const price = cycle === "monthly" ? tier.monthly : tier.yearly;
  const savings = (tier.monthly * 12 - tier.yearly).toFixed(2);
  return (
    <div className="mt-6">
      <div className="flex items-baseline gap-2">
        <span
          data-testid={`${tier.testId}-price`}
          className="font-display text-5xl font-medium tracking-tight text-neutral-900"
        >
          ${price.toFixed(2)}
        </span>
        <span className="text-sm text-neutral-500">/{cycle === "monthly" ? "month" : "year"}</span>
      </div>
      <p className={`mt-2 text-xs font-medium ${cycle === "yearly" ? "text-teal" : "text-neutral-400"}`}>
        {cycle === "yearly" ? `Save $${savings} vs paying monthly` : "Switch to yearly and save 20%"}
      </p>
    </div>
  );
};

const Plans = () => {
  const [cycle, setCycle] = useState("monthly");
  return (
    <section id="plans" data-testid="plans-section" className="bg-cream py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <Reveal><Label>Plans</Label></Reveal>
        <div className="mt-6 flex flex-wrap items-end justify-between gap-8">
          <Reveal delay={0.08}>
            <h2 className="max-w-2xl font-display text-4xl font-medium leading-tight tracking-tight text-neutral-900 md:text-5xl">
              Start free. Grow into <span className="italic text-coral">guidance.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <div
              data-testid="billing-cycle-toggle"
              className="inline-flex items-center rounded-full border border-neutral-200 bg-white p-1.5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
            >
              {["monthly", "yearly"].map((c) => (
                <button
                  key={c}
                  data-testid={`billing-toggle-${c}`}
                  onClick={() => setCycle(c)}
                  className={`relative rounded-full px-5 py-2 text-sm font-semibold capitalize transition-colors duration-200 ${
                    cycle === c ? "text-white" : "text-neutral-500 hover:text-neutral-900"
                  }`}
                >
                  {cycle === c && (
                    <motion.span
                      layoutId="cycle-pill"
                      className="absolute inset-0 rounded-full bg-neutral-900"
                      transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
                    />
                  )}
                  <span className="relative">
                    {c}
                    {c === "yearly" && (
                      <span className={`ml-2 text-xs font-medium ${cycle === c ? "text-teal" : "text-teal"}`}>
                        −20%
                      </span>
                    )}
                  </span>
                </button>
              ))}
            </div>
          </Reveal>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-8">
          {tiers.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.1}>
              <div
                data-testid={t.testId}
                className={`flex h-full flex-col bg-white p-8 transition-shadow duration-300 hover:shadow-[0_24px_48px_rgba(0,0,0,0.08)] ${
                  t.highlight
                    ? "border-2 border-coral shadow-[0_16px_40px_rgba(255,107,87,0.12)] md:-translate-y-3"
                    : "border border-neutral-200/70 shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
                }`}
              >
                <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${t.accent ? "text-teal" : t.highlight ? "text-coral" : "text-neutral-400"}`}>
                  {t.tag}
                </p>
                <h3 className="mt-3 font-display text-3xl font-medium tracking-tight text-neutral-900">{t.name}</h3>
                <p className="mt-3 text-sm leading-relaxed text-neutral-600">{t.blurb}</p>
                <PriceLine tier={t} cycle={cycle} />
                <ul className="mt-8 space-y-3.5 border-t border-neutral-100 pt-8">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-neutral-700">
                      <Check className={`mt-0.5 h-4 w-4 shrink-0 ${t.accent ? "text-teal" : "text-coral"}`} aria-hidden="true" />
                      {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-auto pt-10">
                  <a
                    href={SITE.appStoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`${t.testId}-get-app-link`}
                    className={`inline-flex items-center rounded-full px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] transition-all duration-200 hover:scale-[1.03] ${
                      t.highlight
                        ? "bg-coral text-white shadow-[0_4px_16px_rgba(255,107,87,0.35)] hover:bg-coral-dark"
                        : "bg-neutral-900 text-white hover:shadow-[0_8px_20px_rgba(0,0,0,0.2)]"
                    }`}
                  >
                    Get the App
                  </a>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <p className="mt-10 text-sm leading-relaxed text-neutral-500">
            Prices in USD. Paid plans are billed through your Apple ID, renew automatically, and can be
            managed or cancelled anytime in your Apple ID subscription settings.
          </p>
        </Reveal>
      </div>
    </section>
  );
};

const Closing = () => (
  <section data-testid="closing-section" className="noise relative overflow-hidden bg-neutral-900 py-28 md:py-40">
    <div className="mx-auto max-w-7xl px-6 md:px-10">
      <Reveal>
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-neutral-500">{SITE.name} · iOS</p>
      </Reveal>
      <Reveal delay={0.1}>
        <h2 className="mt-6 max-w-4xl font-display text-4xl font-medium leading-tight tracking-tight text-white md:text-6xl">
          Your next plate is <span className="italic text-coral">already planned.</span>
        </h2>
      </Reveal>
      <Reveal delay={0.2}>
        <div className="mt-12 flex flex-wrap items-center gap-5">
          <AppStoreBadge dark={false} testId="closing-app-store-badge" />
        </div>
      </Reveal>
      <Reveal delay={0.3}>
        <div className="mt-10">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-neutral-500">
            Get NXTPlate news and feature updates
          </p>
          <WaitlistForm source="closing" dark testId="closing-waitlist" />
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <main>
      <Hero />
      <Marquee />
      <Features />
      <Manifesto />
      <Plans />
      <Closing />
    </main>
  );
}
